/**
 * useNarrowedPickerValues (ORCH-389)
 *
 * Purpose: The values one list picker may offer once another field has been
 *          chosen: a Media Subtype picker offers only subtypes of the chosen
 *          media types. The link is the related field on the narrowed list that
 *          holds the other list's value (`fd_media-subtype` values carry
 *          `fd_medium-media-type`), which /authz/dimensions names as
 *          `narrowed_by`. Shared by the membership form and the Role Manager.
 * Logic: Nothing chosen on the other list means nothing to narrow by, so the
 *        picker stays unlimited (no entry for it). With a choice, every active
 *        value of the narrowed list whose tag overlaps the chosen ids is offered,
 *        compared on GLOBAL ids, so a tenant override of Television picks the
 *        same subtypes as Television. An untagged value belongs to no media type
 *        and is left out. Until both lists have loaded, nothing is offered
 *        rather than everything (fails closed, like PickerValues). Reads the
 *        same cached lists the pickers themselves use.
 * Output: limits by the narrowed field's id, in the shape PickerValues takes.
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { mdmValuesQuery } from '../services/mdmCacheService';
import { MasterDataValue } from '../types/mdm';
import { ALL_VALUE, asList, isAll } from '../utils/listValues';

export interface Narrowing {
  /** The list being narrowed: the picker's own field. */
  fielddefId: string;
  /** The list whose chosen values narrow it. */
  byFielddefId: string;
  /** The related field on the narrowed list that holds the other list's value. */
  relatedFielddefId: string;
  /** What is chosen on `byFielddefId`, as stored (GLOBAL or tenant-local ids). */
  chosen: string[];
}

const globalId = (value: MasterDataValue): string => value.global_value_id || value.id;

/** The GLOBAL ids of `values` tagged with one of the chosen `parents` values. */
export const narrowedValues = (
  values: MasterDataValue[],
  parents: MasterDataValue[],
  narrowing: Narrowing
): string[] => {
  const globalOf = new Map(parents.map((parent) => [parent.id, globalId(parent)]));
  const chosen = new Set(narrowing.chosen.map((id) => globalOf.get(id) || id));
  return values
    .filter((value) => value.is_active && !value.is_deleted)
    .filter((value) =>
      // A value tagged ALL on the related list belongs to every chosen value (ORCH-418)
      asList(value.meta_data?.[narrowing.relatedFielddefId]).some(
        (id) => id === ALL_VALUE || chosen.has(globalOf.get(id) || id)
      )
    )
    .map(globalId);
};

const combine = (results: { data?: MasterDataValue[] }[]) => results.map((r) => r.data);

export const useNarrowedPickerValues = (
  narrowings: Narrowing[],
  tenantId: string
): Record<string, string[]> => {
  // Keyed by content: callers build the array on every render
  const activeKey = JSON.stringify(
    narrowings.filter((n) => n.chosen.length > 0 && !isAll(n.chosen))
  );
  const active = useMemo(() => JSON.parse(activeKey) as Narrowing[], [activeKey]);
  const lists = useMemo(
    () => Array.from(new Set(active.flatMap((n) => [n.fielddefId, n.byFielddefId]))),
    [active]
  );
  const loaded = useQueries({
    queries: lists.map((fielddefId) => mdmValuesQuery(fielddefId, tenantId)),
    combine,
  });

  return useMemo(() => {
    const byList = new Map(lists.map((fielddefId, i) => [fielddefId, loaded[i]]));
    const limits: Record<string, string[]> = {};
    active.forEach((narrowing) => {
      const values = byList.get(narrowing.fielddefId);
      const parents = byList.get(narrowing.byFielddefId);
      limits[narrowing.fielddefId] =
        values && parents ? narrowedValues(values, parents, narrowing) : [];
    });
    return limits;
  }, [active, lists, loaded]);
};

export default useNarrowedPickerValues;
